<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  message: { type: Object, required: true },
  currentUserId: { type: Number, default: null },
  canManageMessages: { type: Boolean, default: false },
  canEditOwnMessages: { type: Boolean, default: true },
  showReply: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'delete', 'reply']);

const { t } = useI18n();
const showActions = ref(false);
const menuItems = ref([]);
const hasReplies = computed(() => props.message.replies_count > 0);
const canShowReply = computed(
  () => props.showReply && (!props.message.deleted || hasReplies.value)
);

const isAuthor = () => props.message.sender_id === props.currentUserId;

const withinEditWindow = () => {
  const editableUntil = Date.parse(props.message.editable_until || '');
  return Number.isFinite(editableUntil) && editableUntil > Date.now();
};

const canEditMessage = () =>
  !props.message.deleted &&
  props.canEditOwnMessages &&
  isAuthor() &&
  withinEditWindow();

const canDeleteMessage = () =>
  !props.message.deleted && (props.canManageMessages || isAuthor());

const hasMenuActions = () => canEditMessage() || canDeleteMessage();

const replyLabel = () =>
  hasReplies.value
    ? t('INTERNAL_CHAT.REPLIES', { count: props.message.replies_count })
    : t('INTERNAL_CHAT.REPLY');

const buildMenuItems = () => {
  const items = [];

  if (canEditMessage()) {
    items.push({
      label: t('INTERNAL_CHAT.EDIT'),
      action: 'edit',
      value: 'edit',
      icon: 'i-lucide-pencil-line',
    });
  }

  if (canDeleteMessage()) {
    items.push({
      label: t('INTERNAL_CHAT.DELETE'),
      action: 'delete',
      value: 'delete',
      icon: 'i-lucide-trash-2',
    });
  }

  return items;
};

const closeActions = () => {
  showActions.value = false;
  menuItems.value = [];
};

const toggleActions = () => {
  if (showActions.value) {
    closeActions();
    return;
  }

  menuItems.value = buildMenuItems();
  showActions.value = true;
};

const handleAction = ({ action }) => {
  closeActions();

  if (action === 'edit') emit('edit', props.message);
  if (action === 'delete') emit('delete', props.message);
};
</script>

<template>
  <div
    v-if="!disabled && (canShowReply || hasMenuActions())"
    class="mt-1 flex min-h-6 items-center gap-1"
    :class="
      message.sender_id === currentUserId ? 'justify-end' : 'justify-start'
    "
  >
    <Button
      v-if="canShowReply"
      type="button"
      variant="ghost"
      :color="hasReplies ? 'blue' : 'slate'"
      size="xs"
      :label="replyLabel()"
      :icon="hasReplies ? 'i-lucide-messages-square' : 'i-lucide-reply'"
      class="rounded-md"
      :class="
        hasReplies
          ? 'font-medium'
          : 'opacity-100 transition-opacity md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100'
      "
      @click="emit('reply', message)"
    />
    <div
      v-if="hasMenuActions()"
      v-on-clickaway="closeActions"
      class="relative opacity-100 transition-opacity md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100"
    >
      <Button
        v-tooltip="t('INTERNAL_CHAT.MESSAGE_ACTIONS')"
        type="button"
        variant="ghost"
        color="slate"
        size="xs"
        icon="i-lucide-ellipsis-vertical"
        class="rounded-md"
        :aria-label="t('INTERNAL_CHAT.MESSAGE_ACTIONS')"
        :aria-expanded="showActions"
        aria-haspopup="menu"
        @click="toggleActions"
      />
      <DropdownMenu
        v-if="showActions"
        role="menu"
        :aria-label="t('INTERNAL_CHAT.MESSAGE_ACTIONS')"
        :menu-items="menuItems"
        class="bottom-full mb-1 w-44 ltr:right-0 rtl:left-0"
        @action="handleAction"
      />
    </div>
  </div>
  <span v-else class="hidden" aria-hidden="true" />
</template>
